/**
 * Costanti e helper condivisi fra le pagine dei rilievi fiscali
 * (VerificheList.tsx e VerificheRegolaDetail.tsx).
 *
 * Le etichette di natura_danno/stato sono un lookup di un CHECK di database
 * fisso (migration 0018/0020, verifiche.py:_STATI_VALIDI/_NATURA_DANNO_ORDINE),
 * non un catalogo che cambia a piacere: l'API non espone un endpoint di
 * lookup per queste due enum, quindi la traduzione in italiano resta qui,
 * stesso pattern gia' in uso nel modulo per enum altrettanto fisse
 * (StatoBadge.tsx STATO_LABEL, ArchivioList.tsx TIPI_ARCHIVIO).
 */

/** Ordine e etichette di natura_danno - CATALOGO 0.4, verifiche.py:_NATURA_DANNO_ORDINE. */
export const NATURA_DANNO_ORDINATA: { codice: string; etichetta: string }[] = [
  { codice: 'documento_scartato', etichetta: 'Documento scartato' },
  { codice: 'sanzione', etichetta: 'Sanzione' },
  { codice: 'imposta_errata', etichetta: 'Imposta errata' },
  { codice: 'bilancio_errato', etichetta: 'Bilancio errato' },
  { codice: 'segnalazione', etichetta: 'Segnalazione' },
];

export function etichettaNaturaDanno(codice: string): string {
  return NATURA_DANNO_ORDINATA.find((n) => n.codice === codice)?.etichetta ?? codice;
}

/** Stati validi (_STATI_VALIDI) con etichetta ed etichetta breve per la tab. */
export const STATI_RILIEVO: { codice: string; etichetta: string; gruppo: 'coda' | 'archivio' }[] = [
  { codice: 'aperto', etichetta: 'Aperti', gruppo: 'coda' },
  { codice: 'confermato', etichetta: 'Confermati', gruppo: 'coda' },
  { codice: 'archiviato', etichetta: 'Archiviati', gruppo: 'archivio' },
  { codice: 'risolto_automatico', etichetta: 'Risolti automaticamente', gruppo: 'archivio' },
];

/**
 * Data di oggi (YYYY-MM-DD) dall'orologio LOCALE del browser, composta dalle
 * parti (getFullYear/getMonth/getDate), mai da Date.toISOString() (quello
 * converte in UTC e vicino alla mezzanotte sposta il giorno) e mai
 * ricostruita facendo new Date() su una stringa YYYY-MM-DD (quella la
 * tratta come UTC-mezzanotte: stesso problema, direzione opposta).
 *
 * Serve solo per decidere se una sospensione mostrata a schermo e' ancora
 * "in attesa" o gia' scaduta (una decisione di interfaccia, non fiscale):
 * non e' la data autoritativa del server usata dal backend per i filtri.
 */
export function oggiLocale(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Somma giorni alla data odierna locale, restituisce YYYY-MM-DD. */
export function piuGiorniLocale(giorni: number): string {
  const d = new Date();
  d.setDate(d.getDate() + giorni);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Ultimo giorno del mese corrente (locale), restituisce YYYY-MM-DD. */
export function fineMeseLocale(): string {
  const d = new Date();
  const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const mm = String(ultimo.getMonth() + 1).padStart(2, '0');
  const dd = String(ultimo.getDate()).padStart(2, '0');
  return `${ultimo.getFullYear()}-${mm}-${dd}`;
}

/**
 * Formatta una data ISO SOLO-DATA (YYYY-MM-DD, es. Fattura.data o
 * VerificaFiscale.sospeso_fino) in it-IT componendo dalle parti della
 * stringa: mai new Date('YYYY-MM-DD') (regola HARD del progetto), che la
 * interpreta come UTC-mezzanotte e sposta il giorno secondo il fuso del
 * browser. Per i timestamp (created_at/deciso_at, che portano l'offset)
 * usare invece useFormatters().dateTime, sicuro perche' l'offset e'
 * esplicito nella stringa.
 */
export function formattaDataSolo(isoData: string): string {
  const [anno, mese, giorno] = isoData.split('-');
  if (!anno || !mese || !giorno) return isoData;
  return `${giorno}/${mese}/${anno}`;
}
